// ─────────────────────────────────────────────────────────────────────────────
// AuthedMedia — CA-5 (F-09) frontend companion (CR-2026-FE-AUTHED-TASK-MEDIA).
//
// The backend gates /uploads/task_images + /uploads/task_pdfs (and already gates
// /uploads/documents) behind a JWT Bearer check. Plain <img src="/uploads/..">
// tags do NOT send the Authorization header (the token lives in localStorage,
// not a cookie), so gated media 401s. These components fetch the bytes WITH the
// Bearer header via axios (responseType:'blob'), then render/open an object URL.
// S-68 (2026-10-06): the backend also gates /uploads/job_drawings (incl.
// superseded/) and checks that the file belongs to the signed-in user's company
// (another company's file is 404). The job drawing viewer (PDF.js) and the task
// download button use fetchAuthedMedia / downloadAuthedMedia below.
//
// Passthrough rule: data: and blob: URLs (fresh local uploads / previews) render
// directly with no fetch. Everything else (same-origin /uploads paths, absolute
// shopfloormgmt.com URLs) is fetched; the Bearer header is attached only for our
// own origin's /uploads/ paths (see mayCarryToken), so a URL elsewhere is fetched
// without the session token.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { getAccessToken } from "../../utils/authToken";
import { showErrorToast } from "../../utils/Toast";

function isPassthrough(src) {
  return typeof src === "string" && (src.startsWith("data:") || src.startsWith("blob:"));
}

// S-68 (2026-10-06): media requests time out like ApiCall calls (30 s) instead of
// hanging a thumbnail or the drawing viewer forever on a stalled network.
const MEDIA_TIMEOUT_MS = 30000;

// Fetch a gated /uploads URL with the Bearer token. responseType "blob" or
// "arraybuffer". Throws on any non-2xx (e.g. 401 signed out, 404 another
// company's file), so callers can show an error state.
// S-68 (Codex #74 P1): the session token goes ONLY to our own server's /uploads/ files. The URL comes
// from server data (task.image, job.drawing_pdf_url, document file_url); if one ever pointed elsewhere,
// the token must not travel with it. "Our server" = this page's origin or the API origin (REACT_APP_URL).
function ownOrigins() {
  const set = new Set();
  try { set.add(window.location.origin); } catch (_) { /* no window */ }
  try { if (process.env.REACT_APP_URL) set.add(new URL(process.env.REACT_APP_URL, window.location.href).origin); } catch (_) { /* bad env */ }
  return set;
}
export function mayCarryToken(src) {
  try {
    const u = new URL(String(src), window.location.href);
    return (u.protocol === "https:" || u.protocol === "http:") && ownOrigins().has(u.origin) && u.pathname.startsWith("/uploads/");
  } catch (_) {
    return false;
  }
}

export async function fetchAuthedMedia(src, responseType = "blob") {
  if (!src) throw new Error("no src");
  const token = mayCarryToken(src) ? getAccessToken() : null;
  const res = await axios.get(src, {
    responseType,
    timeout: MEDIA_TIMEOUT_MS,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return res.data;
}

// Fetch a URL as a blob with the Bearer token; returns an object URL (caller
// revokes) or throws. Passthrough URLs are returned unchanged.
export async function fetchAuthedObjectUrl(src) {
  if (!src) throw new Error("no src");
  if (isPassthrough(src)) return { url: src, revoke: false };
  const blob = await fetchAuthedMedia(src, "blob");
  return { url: URL.createObjectURL(blob), revoke: true };
}

// S-74 (2026-10-07): a blob: URL runs in OUR origin. Opening (or embedding) a blob whose type is HTML, SVG,
// XML or anything else a browser renders as a document would let an uploaded file run script with the
// user's session. Only these types are ever shown in a tab or an <object>; everything else is downloaded
// as an opaque octet-stream. (Uploads are already limited by SEC-007; this is defence in depth.)
export function viewableKind(type) {
  const t = String(type || "").split(";")[0].trim().toLowerCase();
  if (t === "application/pdf") return "pdf";
  if (/^image\/(png|jpe?g|gif|webp|bmp|avif)$/.test(t)) return "image";
  return null;
}
function fileNameOf(src) {
  try { return decodeURIComponent(String(src).split("?")[0].split("/").pop() || "download"); }
  catch (_) { return "download"; }
}

// S-68: save gated media to disk. A plain fetch()/<a href> sends no Bearer
// header, so once the gate is on it would save the 401 JSON body as the file.
// Returns true on success, false on failure (caller shows the error).
export async function downloadAuthedMedia(src, filename) {
  if (!src) return false;
  let url = null;
  let revoke = false;
  try {
    if (isPassthrough(src)) { url = src; revoke = false; }
    else {
      // S-74: a download is always an opaque octet-stream blob, so nothing it contains can render.
      const blob = await fetchAuthedMedia(src, "blob");
      url = URL.createObjectURL(new Blob([blob], { type: "application/octet-stream" }));
      revoke = true;
    }
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || fileNameOf(src);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return true;
  } catch (e) {
    return false;
  } finally {
    // Give the browser a moment to start the download before freeing the blob.
    if (revoke && url) setTimeout(() => URL.revokeObjectURL(url), 10000);
  }
}

// Open gated media in a new tab: window.open can't send a Bearer header, so we
// fetch the blob (authed) and open the object URL instead.
// W-29 (Codex #80 P1): resolves true when the file was fetched and shown or downloaded, false when the
// fetch failed (the error toast is shown here). Callers that must know the user received the file
// (CMTR review) use the result.
export async function openAuthedMedia(src) {
  if (!src) return false;
  if (isPassthrough(src)) { window.open(src, "_blank", "noopener,noreferrer"); return true; }
  // S-70 (Codex #78 P1): reserve the tab synchronously, inside the click, so a slow
  // download cannot outlast the browser's user activation and get the popup blocked.
  // The tab is about:blank (our origin) until the blob is ready; its opener is cut
  // right away. If the browser blocked even this, fall back to a download.
  let win = null;
  try { win = window.open("", "_blank"); if (win) win.opener = null; } catch (_) { win = null; }
  try {
    const blob = await fetchAuthedMedia(src, "blob");
    const kind = viewableKind(blob.type);
    // S-74: only a PDF or a raster image is shown in the tab, re-typed to exactly that type; anything else
    // (HTML, SVG, unknown) is downloaded as application/octet-stream and the reserved tab is closed.
    const typed = kind ? new Blob([blob], { type: String(blob.type).split(";")[0].trim().toLowerCase() })
                       : new Blob([blob], { type: "application/octet-stream" });
    const url = URL.createObjectURL(typed);
    if (kind && win && !win.closed) {
      win.location.replace(url);
    } else {
      if (win && !win.closed) { try { win.close(); } catch (_) { /* already gone */ } }
      const a = document.createElement("a");
      a.href = url;
      a.download = fileNameOf(src);
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    }
    // Revoke after the tab (or download) has had time to load the blob.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  } catch (e) {
    if (win && !win.closed) { try { win.close(); } catch (_) { /* already gone */ } }
    // S-68: no direct-URL fallback. Once the backend gate is on, the plain URL
    // only opens a 401/404 JSON page; tell the user instead.
    const st = e?.response?.status;
    showErrorToast(
      st === 401 || st === 403
        ? "Couldn't open the file: please sign in again."
        : st === 404
        ? "File not found, or it belongs to another company."
        : "Couldn't open the file. Please try again."
    );
    return false;
  }
}

// expect: "image" (for <img>) or "pdf" (for <object>). S-74: a fetched blob of any other type is an
// error state, never rendered (an <object> would render an HTML blob in our origin).
function useAuthedUrl(src, expect) {
  const [state, setState] = useState({ url: null, error: false });
  useEffect(() => {
    let cancelled = false;
    let toRevoke = null;
    if (!src) { setState({ url: null, error: false }); return undefined; }
    if (isPassthrough(src)) { setState({ url: src, error: false }); return undefined; }
    setState({ url: null, error: false });
    fetchAuthedMedia(src, "blob")
      .then((blob) => {
        if (cancelled) return;
        if (viewableKind(blob.type) !== expect) { setState({ url: null, error: true }); return; }
        const url = URL.createObjectURL(new Blob([blob], { type: String(blob.type).split(";")[0].trim().toLowerCase() }));
        toRevoke = url;
        setState({ url, error: false });
      })
      .catch(() => { if (!cancelled) setState({ url: null, error: true }); });
    return () => { cancelled = true; if (toRevoke) URL.revokeObjectURL(toRevoke); };
  }, [src, expect]);
  return state;
}

export function AuthedImage({ src, alt = "", style, className, title, onClick }) {
  const { url, error } = useAuthedUrl(src, "image");
  if (error) {
    return (
      <span
        title="Media unavailable"
        style={{ ...style, display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: "#f4f4f4", color: "#999", fontSize: 10, border: "1px solid #e0e0e0" }}
        className={className}
      >
        n/a
      </span>
    );
  }
  if (!url) {
    return (
      <span
        aria-busy="true"
        style={{ ...style, display: "inline-block", background: "#f0f0f0", borderRadius: 4 }}
        className={className}
      />
    );
  }
  return <img src={url} alt={alt} style={style} className={className} title={title} onClick={onClick} />;
}

AuthedImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
  title: PropTypes.string,
  onClick: PropTypes.func,
};

export function AuthedObject({ src, style, ariaLabel = "PDF preview", onClick, pdfParams = "#toolbar=1&navpanes=1&view=FitH" }) {
  const { url, error } = useAuthedUrl(src, "pdf");
  if (error) {
    return (
      <div onClick={onClick} style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center",
        background: "#f4f4f4", color: "#999", fontSize: 13, border: "1px solid #e0e0e0" }}>
        PDF unavailable
      </div>
    );
  }
  if (!url) {
    return <div aria-busy="true" style={{ ...style, background: "#f0f0f0", borderRadius: 4 }} />;
  }
  return (
    <object data={`${url}${pdfParams}`} type="application/pdf" aria-label={ariaLabel} style={style} onClick={onClick}>
      <button type="button" onClick={() => openAuthedMedia(src)}
        style={{ padding: 8, cursor: "pointer" }}>
        Open PDF
      </button>
    </object>
  );
}

AuthedObject.propTypes = {
  src: PropTypes.string,
  style: PropTypes.object,
  ariaLabel: PropTypes.string,
  onClick: PropTypes.func,
  pdfParams: PropTypes.string,
};

export default AuthedImage;
